import { Injectable, computed, signal } from "@angular/core";
import { HITL_NONE_OPTION_ID } from "../model/interrupt.types";
import type { InterruptItem, HitlResumePayload, InterruptPayload } from "../model/interrupt.types";

/**
 * Interrupt state manager for LangGraph interrupts.
 *
 * Both interrupt types (`clarification` and `frontend_tools`) are activated via
 * activate(payload) and cleared via clear(). The chat container calls these after
 * each runAgent completes — never mid-stream. The wizard and tool-call card
 * read state directly from this service; no intermediate signal is needed.
 */
@Injectable({ providedIn: "root" })
export class HitlService {
  private readonly _payload = signal<InterruptPayload | null>(null);

  private readonly _currentIdx = signal<number>(0);

  private readonly _answers = signal<HitlResumePayload>([]);

  private readonly _frontendToolsPayload = signal<InterruptPayload | null>(null);

  /** The interrupt item the user must answer right now, or null when no clarification is active. */
  readonly currentEntry = computed<InterruptItem | null>(() => {
    const p = this._payload();
    if (!p) return null;
    return p.items[this._currentIdx()] ?? null;
  });

  /** Non-null while frontend_tools auto-execution is in progress. */
  readonly frontendToolsPayload = this._frontendToolsPayload.asReadonly();

  /** True while an interrupt is active (clarification or frontend_tools). */
  readonly isBlocked = computed(() => this.currentEntry() !== null || this._frontendToolsPayload() !== null);

  /** Intro text shown above all wizard steps. */
  readonly displayText = computed(() => this._payload()?.display_text ?? "");

  /** True when there are multiple UI entries (shows the progress indicator). */
  readonly showProgress = computed(() => (this._payload()?.items.length ?? 0) > 1);

  /** True once every active entry has a collected answer (the resume payload is ready to emit). */
  readonly allAnswered = computed(
    () => this._payload() !== null && this._answers().length >= this._payload()!.items.length,
  );

  /** 1-based index of the current step (for display: "Step X of N"). */
  readonly currentStep = computed(() => this._currentIdx() + 1);

  /** Total number of UI entries in the active interrupt. */
  readonly totalEntries = computed(() => this._payload()?.items.length ?? 0);

  /**
   * Starts a new clarification wizard for the given payload.
   * tool_call items are filtered out — they are not presented to the user.
   * Resets the step index and clears any previously collected answers.
   */
  setPending(payload: InterruptPayload): void {
    const uiItems = payload.items.filter((i) => i.kind !== "tool_call");
    this._payload.set({ ...payload, items: uiItems });
    this._currentIdx.set(0);
    this._answers.set([]);
  }

  /**
   * Records the answer to the current entry.
   * - If more entries remain, advances to the next step and returns null.
   * - If this was the last entry, returns the complete HitlResumePayload.
   *   The caller must send that payload as Command.resume and then call resolve().
   */
  submitAnswer(value: string | string[]): HitlResumePayload | null {
    const entry = this.currentEntry();
    if (!entry) return null;

    const updated: HitlResumePayload = [...this._answers(), { id: entry.id, value }];
    this._answers.set(updated);

    const nextIdx = this._currentIdx() + 1;
    if (nextIdx >= (this._payload()?.items.length ?? 0)) {
      return updated;
    }

    this._currentIdx.set(nextIdx);
    return null;
  }

  /**
   * Cancels the current ask with the reserved refusal sentinel (HITL contract §4):
   * regardless of kind, the answer value is `__none__` for the current item and the
   * wizard ends immediately. The engine treats this as a user refusal — the task
   * closes FAILED, remaining asks never fire and the plan ends cancelled — so the
   * returned payload is exactly ONE entry, never the accumulated answers.
   */
  cancelCurrent(): HitlResumePayload | null {
    const entry = this.currentEntry();
    if (!entry) return null;

    const payload: HitlResumePayload = [{ id: entry.id, value: HITL_NONE_OPTION_ID }];
    this.resolve();
    return payload;
  }

  /** Clears clarification wizard state. Called after the resume has been sent. */
  resolve(): void {
    this._payload.set(null);
    this._currentIdx.set(0);
    this._answers.set([]);
  }

  /**
   * Activates an interrupt — starts the clarification wizard or shows the tool-call card.
   * Called by the chat container after each run completes with a pending interrupt.
   */
  activate(payload: InterruptPayload): void {
    if (payload.type === "clarification") {
      this.setPending(payload);
    } else if (payload.type === "frontend_tools") {
      this._frontendToolsPayload.set(payload);
    }
  }

  /** Clears only the frontend-tools card. Called after tools finish executing, before sending the resume command. */
  clearFrontendTools(): void {
    this._frontendToolsPayload.set(null);
  }

  /**
   * Clears all interrupt state. Safety net called when a run completes with no
   * interrupt in the new state — ensures nothing lingers if a clear was missed.
   */
  clear(): void {
    this.resolve();
    this._frontendToolsPayload.set(null);
  }
}
